<script setup lang="ts">
// Métadonnées de la page des recettes
import { LazyFilterModal, LazyFridgeCleanerModal } from '#components'

useHead({
  title: 'Recettes - Glouton',
  meta: [
    { name: 'description', content: 'Explorez plus de 20000 recettes pour tous les goûts. Trouvez des recettes économiques et délicieuses.' },
    { property: 'og:title', content: 'Recettes - Glouton' },
    { property: 'og:description', content: 'Explorez plus de 20000 recettes pour tous les goûts. Trouvez des recettes économiques et délicieuses.' },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/recipes',
    fr: '/recettes',
  },
})

definePageMeta({
  name: 'RecipesPage',
})

const userStore = useUserStore()
const config = useRuntimeConfig()
const modalStore = useModalStore()
const route = useRoute()
const router = useRouter()
const overlay = useOverlayWithAd()
const filterStore = useFilterStore()
const localePath = useLocalePath()
const eventBus = useEventBusStore()
const baseStore = useBaseStore()

const page = ref(Number.parseInt(route.query.page) || 1)

// your explicit refs
const recipes = ref([])
const totalPages = ref(1)
const totalResults = ref(0)

// Fridge cleaner state
const selectedIngredients = ref([])
const isFridgeMode = ref(false)
const savedFridgeModeState = ref(false)

// Fonction pour sauvegarder l'état du mode vide-frigo dans localStorage
function saveFridgeModeToStorage() {
  if (import.meta.client) {
    localStorage.setItem('fridgeModeActive', isFridgeMode.value.toString())
  }
}

// Fonction pour récupérer l'état du mode vide-frigo depuis localStorage
function getFridgeModeFromStorage() {
  if (import.meta.client) {
    const saved = localStorage.getItem('fridgeModeActive')
    return saved === 'true'
  }
  return false
}

// Dropdown actions for fridge cleaner
const fridgeCleanerActions = computed(() => {
  const actions = []

  // Afficher "Activer le vide-frigo" seulement si des ingrédients sont sélectionnés
  if (selectedIngredients.value.length > 0) {
    actions.push({
      label: isFridgeMode.value ? 'Désactiver le vide-frigo' : 'Activer le vide-frigo',
      type: 'checkbox' as const,
      checked: isFridgeMode.value,
      onUpdateChecked(checked: boolean) {
        isFridgeMode.value = checked
      },
    })
  }

  actions.push({
    label: 'Choisir mes ingrédients',
    trailing: selectedIngredients.value.length > 0 ? selectedIngredients.value.length.toString() : undefined,
    onSelect() {
      openFridgeCleanerModal()
    },
  })

  return actions
})

const tabs = computed(() => {
  return [
    {
      label: 'Toutes les recettes',
      to: localePath({ name: 'RecipesPage' }),
      active: route.name === 'RecipesPage___fr',
      extra: false,
    },
    {
      label: 'L\'économiseur',
      to: localePath({ name: 'MoneySaverPage' }),
      active: route.name === 'MoneySaverPage___fr',
      extra: true,
    },
  ]
})

// Computed pour les IDs des ingrédients
const ingredientIds = computed(() => {
  if (!selectedIngredients.value || selectedIngredients.value.length === 0) {
    return []
  }
  return selectedIngredients.value.map((ingredient: any) => ingredient.id)
})

// your normal fetch function
async function fetchRecipes() {
  if (isFridgeMode.value && selectedIngredients.value.length > 0) {
    // Utiliser l'endpoint fridge-cleaner
    return await $fetch(`${config.public.apiUrl}/fridge-cleaner/fetch-recipes`, {
      query: {
        'ings[]': ingredientIds.value,
        'page': page.value,
        'filters[]': filterStore.filters,
        'strict': filterStore.strict,
      },
    })
  }
  else {
    // Utiliser l'endpoint normal
    return await $fetch(`${config.public.apiUrl}/recipes/explore`, {
      query: {
        'page': page.value,
        'strict': filterStore.strict,
        'filters[]': filterStore.filters,
      },
    })
  }
}

// call useAsyncData for SSR + hydration
const { data, pending, refresh } = await useAsyncData(
  `recipes-${page.value}-${isFridgeMode.value}-${ingredientIds.value.join(',')}`,
  () => fetchRecipes(),
  { watch: [page, isFridgeMode, ingredientIds] },
)

// now explicitly set your refs whenever data updates
watch(
  data,
  (newData) => {
    if (newData) {
      recipes.value = newData.data || newData.recipes?.data || []
      totalPages.value = newData.last_page || newData.recipes?.last_page || 1
      totalResults.value = newData.total || newData.recipes?.total || 0

      // Track recipe prints on client side when recipes are loaded
      if (import.meta.client && recipes.value.length > 0) {
        baseStore.trackRecipePrints(recipes.value, 'recipe_listing').catch(() => {
          // Silent fail - tracking shouldn't block the UI
        })
      }
    }
  },
  { immediate: true },
)

// watch URL changes (back/forward) to keep `page` in sync
watch(
  () => route.query.page,
  (newPage) => {
    const parsed = Number.parseInt(newPage) || 1
    if (parsed !== page.value)
      page.value = parsed
  },
)

// update URL when `page` changes
watch(page, (newVal) => {
  router.push({ query: { ...route.query, page: newVal } })

  if (import.meta.client) {
    const el = document.getElementById('RecipesPage-anchor')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

async function openFilterModal() {
  const modal = overlay.create(LazyFilterModal)
  const modalInstance = modal.open()
  const input = await modalInstance.result
  if (input !== false) {
    page.value = 1
    await refresh()
  }
}

async function openFridgeCleanerModal() {
  const modal = overlay.create(LazyFridgeCleanerModal, {
    props: {
      initialIngredients: selectedIngredients.value,
    },
  })
  modal.open()
}

// Chargement initial si l'utilisateur est déjà connecté
onMounted(async () => {
  // Si l'utilisateur est connecté, récupérer ses ingrédients sauvegardés
  if (userStore.user) {
    await fetchUserIngredients()
    // Restaurer l'état du mode vide-frigo depuis localStorage
    const savedMode = getFridgeModeFromStorage()
    if (savedMode && selectedIngredients.value.length > 0) {
      isFridgeMode.value = true
    }
  }
  else {
    // Pour les utilisateurs non connectés, récupérer depuis localStorage
    const savedMode = getFridgeModeFromStorage()
    if (savedMode && selectedIngredients.value.length > 0) {
      isFridgeMode.value = true
    }
  }

  refresh()
})

// Watcher pour détecter quand l'utilisateur se connecte
watch(() => userStore.user, async (newUser) => {
  if (newUser) {
    await fetchUserIngredients()
  }
})

// Watcher pour sauvegarder l'état quand le mode change
watch(isFridgeMode, async () => {
  // Sauvegarder dans localStorage
  saveFridgeModeToStorage()
})

// Watcher pour désactiver le mode vide-frigo si aucun ingrédient n'est sélectionné
watch(selectedIngredients, (newIngredients) => {
  // Désactiver le mode seulement si aucun ingrédient n'est sélectionné
  if (newIngredients.length === 0) {
    isFridgeMode.value = false
  }
})

// Watcher pour les événements de l'event bus
watch(
  () => eventBus.lastEvent,
  (event) => {
    if (event === 'fridge-cleaner:cleared') {
      // Vider la liste des ingrédients sélectionnés
      selectedIngredients.value = []
      isFridgeMode.value = false
      page.value = 1
      refresh()
    }
    else if (event === 'fridge-cleaner:updated') {
      // Recharger les ingrédients depuis le serveur si l'utilisateur est connecté
      if (userStore.user) {
        fetchUserIngredients()
      }
      page.value = 1
      refresh()
    }
    else if (event === 'fridge-cleaner:find-recipes') {
      // Activer le mode vide-frigo s'il y a des ingrédients sélectionnés
      if (selectedIngredients.value.length > 0) {
        isFridgeMode.value = true
        page.value = 1
        refresh()
      }
    }
  },
)

// Fonction pour récupérer les ingrédients sauvegardés de l'utilisateur
async function fetchUserIngredients() {
  try {
    const response = await $fetch(`${config.public.apiUrl}/fridge-cleaner/fetch-user-ingredients`, {
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.ingredients && response.ingredients.length > 0) {
      selectedIngredients.value = response.ingredients
      // Sauvegarder l'état du mode vide-frigo
      savedFridgeModeState.value = response.is_fridge_mode_active || false
    }
  }
  catch (err) {
    console.error('Erreur lors de la récupération des ingrédients:', err)
  }
}
</script>

<template>
  <div id="RecipesPage" :class="{ 'fridge-mode-active': isFridgeMode }" class="mt-0">
    <TopNav
      title="Recettes & vide-frigo"
      description="Découvrez des recettes librement ou avec des filtres de thématiques et restrictions alimentaires." access="Gratuit pour tous"
      :total="totalResults"
      :tabs="tabs"
      class="max-w-7xl mx-auto"
      :count="totalResults"
    />
    <UContainer id="RecipesPage-anchor" class="pb-10">
      <!-- Bannière de promotion -->

      <PromoBanner class="my-6" message-type="message_recipes" />

      <div class="my-6 space-x-2">
        <UButton color="secondary" icon="i-mdi-tune" @click="openFilterModal">
          Filtrer <UBadge v-if="filterStore.filters.length > 0" size="sm" variant="soft" color="white" class="">
            {{ filterStore.filters.length }}
          </UBadge>
        </UButton>
        <UDropdownMenu :items="fridgeCleanerActions" :content="{ align: 'start', sideOffset: 1 }" :ui="{ content: 'w-68' }" class="group">
          <UButton
            :color="isFridgeMode ? 'primary' : 'secondary'"
            icon="i-mdi-fridge"
            trailing-icon="i-mdi-chevron-down"
            :ui="{
              trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200',
            }"
          >
            Vide-frigo
            <UBadge v-if="selectedIngredients && selectedIngredients.length > 0" size="sm" variant="soft" color="white" class="">
              {{ selectedIngredients.length }}
            </UBadge>
          </UButton>
        </UDropdownMenu>
      </div>

      <!-- Affichage des recettes -->
      <div v-if="recipes.length > 0" class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
        <div class="grid grid-cols-1 gap-6 pb-4 sm:grid-cols-2  lg:grid-cols-4">
          <template v-for="(recipe, index) in recipes" :key="recipe.id">
            <div class="flex flex-col">
              <RecipeCard :recipe="recipe" />
            </div>
            <div
              v-if="[2, 6].includes(index + 1) && baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
              class="flex flex-col sm:col-span-2 md:col-span-1 lg:col-span-2"
            >
              <DisplayAd
                dfp-ad-unit-path="glouton/recettes"
                :sizes="[300, 250]"
                custom-class="h-full"
              />
            </div>
          </template>
        </div>
      </div>

      <!-- Message si aucun résultat en mode vide-frigo -->
      <div v-else-if="isFridgeMode && selectedIngredients.length > 0 && recipes.length === 0 && !pending" class="text-center py-12">
        <UIcon name="i-mdi-fridge-outline" class="w-16 h-16 mx-auto text-poivre-400 mb-4" />
        <h3 class="text-lg font-medium text-poivre-800 mb-2">
          Aucune recette trouvée
        </h3>
        <p class="text-poivre-600 mb-4">
          Aucune recette ne correspond aux ingrédients sélectionnés.
        </p>
        <UButton color="secondary" @click="openFridgeCleanerModal">
          Modifier les ingrédients
        </UButton>
      </div>

      <!-- Message si aucun ingrédient sélectionné en mode vide-frigo -->
      <div v-else-if="isFridgeMode && selectedIngredients.length === 0" class="text-center py-12">
        <UIcon name="i-mdi-fridge-outline" class="w-16 h-16 mx-auto text-poivre-400 mb-4" />
        <h3 class="text-lg font-medium text-poivre-800 mb-2">
          Sélectionnez des ingrédients
        </h3>
        <p class="text-poivre-600 mb-4">
          Ajoutez des ingrédients que vous avez dans votre frigo pour trouver des recettes.
        </p>
        <UButton color="primary" @click="openFridgeCleanerModal">
          Ajouter des ingrédients
        </UButton>
      </div>
      <!-- Pagination -->
      <DisplayAd
        v-if="baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
        dfp-ad-unit-path="glouton/recettes"
        :sizes="[[320, 50], [728, 90]]"
        :size-mapping="[[[0, 0], [[320, 50]]], [[1024, 0], [[728, 90]]]]"
        custom-class="mb-6"
      />
      <Paginator
        v-if="recipes.length > 0"
        class="flex justify-center mt-4"
        :total-pages="totalPages"
        :total="totalResults"
        :per-page="12"
        :current-page="page"
        @pagechanged="(newPage) => page = newPage"
      />
    </UContainer>
  </div>
</template>

<style scoped>
#RecipesPage.fridge-mode-active {
  background: linear-gradient(180deg, #faebe5 0%, #f4f4f6 100%);
}
</style>
